/**
 * Multi Image Capture Component
 *
 * Permite capturar múltiples imágenes desde cámara o seleccionar archivos.
 * Basado en ImageCapture pero con soporte para múltiples fotos.
 */

import { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Upload, X, Image as ImageIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface CapturedImage {
  file: File;
  preview: string;
}

interface MultiImageCaptureProps {
  value: CapturedImage[];
  onChange: (images: CapturedImage[]) => void;
  className?: string;
  disabled?: boolean;
  maxImages?: number;
}

export function MultiImageCapture({
  value,
  onChange,
  className,
  disabled = false,
  maxImages = 20,
}: MultiImageCaptureProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  // Cleanup: stop camera stream when component unmounts
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      const newImages: CapturedImage[] = [];
      let processed = 0;

      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          newImages.push({
            file,
            preview: event.target?.result as string,
          });
          processed++;

          if (processed === files.length) {
            const combined = [...value, ...newImages].slice(0, maxImages);
            onChange(combined);
          }
        };
        reader.readAsDataURL(file);
      });

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [value, onChange, maxImages]
  );

  const openCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: 1280, height: 720 },
      });
      setStream(mediaStream);
      setIsCameraOpen(true);

      // Wait for video element to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play();
        }
      }, 100);
    } catch {
      // Camera not available, fallback to file input
      fileInputRef.current?.click();
    }
  }, []);

  const closeCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setIsCameraOpen(false);
  }, [stream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    if (value.length >= maxImages) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!context) return;

    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0);

    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
          const file = new File([blob], `foto_${timestamp}.jpg`, {
            type: "image/jpeg",
          });
          const preview = canvas.toDataURL("image/jpeg");

          onChange([...value, { file, preview }]);
        }
        // Don't close camera - allow multiple captures
      },
      "image/jpeg",
      0.9
    );
  }, [value, onChange, maxImages]);

  const removeImage = useCallback(
    (index: number) => {
      const newImages = value.filter((_, i) => i !== index);
      onChange(newImages);
    },
    [value, onChange]
  );

  const clearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <div className={cn("space-y-4", className)}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Camera view */}
      {isCameraOpen && (
        <div className="relative rounded-lg overflow-hidden bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full max-h-[300px] object-contain"
          />
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
            <Button type="button" size="sm" onClick={capturePhoto}>
              <Camera className="mr-2 h-4 w-4" />
              {t("archivosAdjuntos.capture")} ({value.length}/{maxImages})
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={closeCamera}>
              {t("archivosAdjuntos.closeCamera")}
            </Button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!isCameraOpen && !disabled && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={openCamera}
            disabled={value.length >= maxImages}
          >
            <Camera className="mr-2 h-4 w-4" />
            {t("archivosAdjuntos.openCamera")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
            disabled={value.length >= maxImages}
          >
            <Upload className="mr-2 h-4 w-4" />
            {t("archivosAdjuntos.uploadPhotos")}
          </Button>
        </div>
      )}

      {/* Image grid */}
      {value.length > 0 ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {t("archivosAdjuntos.capturedPhotos")} ({value.length})
            </span>
            {!disabled && value.length > 1 && (
              <Button type="button" variant="ghost" size="sm" onClick={clearAll}>
                <X className="mr-1 h-3 w-3" />
                {t("common.delete")} {t("common.all") || "todas"}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {value.map((image, index) => (
              <div key={index} className="relative group">
                <img
                  src={image.preview}
                  alt={`Foto ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg border"
                />
                {!disabled && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => removeImage(index)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        !isCameraOpen && (
          <div
            className={cn(
              "rounded-lg border-2 border-dashed p-8 transition-colors",
              "border-muted-foreground/25"
            )}
          >
            <div className="flex flex-col items-center justify-center text-muted-foreground">
              <ImageIcon className="h-8 w-8 mb-2" />
              <p className="text-sm">{t("archivosAdjuntos.noArchivos")}</p>
            </div>
          </div>
        )
      )}
    </div>
  );
}
